import { usePostHog } from 'posthog-js/react'
import { useCallback, useRef } from 'react'

/**
 * Rejects object keys that contain underscores or hyphens (i.e., not camelCase).
 * When a non-camelCase key is used, its value type becomes an error tuple,
 * causing a compile-time failure at the call site.
 */
type AssertCamelCaseKeys<T> = {
  [K in keyof T]: K extends `${string}_${string}` | `${string}-${string}`
    ? ['Error: property key must be camelCase, got:', K]
    : T[K]
}

type EventMap = Record<string, undefined | Record<string, unknown>>

type TrackOptions = {
  /**
   * By default, all string property values are lowercased before sending to PostHog.
   * Set to false to send property values as-is (preserving original casing).
   */
  lowercase?: boolean
}

const defaultTrackOptions: Required<TrackOptions> = {
  lowercase: true,
}

const normalizeProperties = (
  properties: Record<string, unknown> | undefined,
  options: Required<TrackOptions>
): Record<string, unknown> | undefined => {
  if (!properties) {
    return undefined
  }
  if (!options.lowercase) {
    return properties
  }

  const normalized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(properties)) {
    normalized[key] = typeof value === 'string' ? value.toLowerCase() : value
  }
  return normalized
}

type ValidateProps<T> = T extends Record<string, unknown> ? AssertCamelCaseKeys<T> : T

type TrackArgs<T extends EventMap, E extends keyof T & string> = T[E] extends undefined
  ? [event: E] | [event: E, properties: null, options: TrackOptions]
  :
      | [event: E, properties: ValidateProps<T[E]>]
      | [event: E, properties: ValidateProps<T[E]>, options: TrackOptions]

type TrackFn<T extends EventMap> = <E extends keyof T & string>(...args: TrackArgs<T, E>) => void

/**
 * Generic, type-safe PostHog event tracking hook.
 *
 * Parameterize with an app-specific event map to get autocomplete on event
 * names and compile-time enforcement of required properties.
 *
 * - Void events (`undefined` in the map): `track('Event name')`
 * - Property events: `track('Event name', { ...props })`
 *
 * String property values are lowercased by default.
 * Pass `null` as properties and `{ lowercase: false }` as the third argument
 * to preserve original casing for void events, or pass options as the third
 * argument for property events.
 *
 * The returned function is **referentially stable** across renders.
 *
 * @example
 * ```ts
 * type MyEvents = {
 *   'Button clicked': undefined
 *   'Item created': { type: string }
 * }
 *
 * const track = useTrack<MyEvents>()
 *
 * track('Button clicked')
 * track('Button clicked', null, { lowercase: false })
 * track('Item created', { type: 'widget' })
 * track('Item created', { type: 'Widget' }, { lowercase: false })
 * ```
 */
export const useTrack = <T extends EventMap>(): TrackFn<T> => {
  const posthog = usePostHog()

  const trackRef = useRef<TrackFn<T>>(null as unknown as TrackFn<T>)
  trackRef.current = ((...args: unknown[]) => {
    const [event, properties, options] = args as [
      string,
      Record<string, unknown> | null | undefined,
      TrackOptions | undefined,
    ]
    const opts = { ...defaultTrackOptions, ...options }
    posthog.capture(event, normalizeProperties(properties ?? undefined, opts))
  }) as TrackFn<T>

  return useCallback(
    ((...args: unknown[]) => {
      ;(trackRef.current as (...a: unknown[]) => void)(...args)
    }) as TrackFn<T>,
    []
  )
}
